import { Organization } from "./organization"
import {
  MetaData,
  ProjectTargetGroup,
  ProjectType,
  LessonDeadlineTrigger,
  ProjectRoute,
  Subcontractor,
  SubsidyRegulation,
  ReportingFrequencyTrigger,
  ReportingTimeUnit,
  ExamTypeOwner,
} from "toptaal-shared"
import { SimpleUser, Document } from "./user"
import z from "zod"

type RegionManager = {
  id: number
} & Omit<SimpleUser, "isTeacher" | "isFreelance">

type ProjectCoordinator = {
  id: number
} & Omit<SimpleUser, "isTeacher" | "isFreelance">

type ProjectPlanner = {
  id: number
} & Omit<SimpleUser, "isTeacher" | "isFreelance">

type AdministrativeEmployee = {
  id: number
} & Omit<SimpleUser, "isTeacher" | "isFreelance">

interface ProjectReportSettings {
  isReportingRequired: boolean | null
  isStudentSignatureRequired: boolean | null
  reportCategory: ProjectReportCategory | null
  templateIntakeActive: boolean
  templateIntakeReportType: string | null
  templateScalingDownActive: boolean
  templateScalingDownReportType: string | null
  templateProgressActive: boolean
  templateProgressReportType: string | null
  templateChangeRouteActive: boolean
  templateChangeRouteReportType: string | null
  templateEndActive: boolean
  templateEndReportType: string | null
  templateDropoutActive: boolean
  templateDropoutReportType: string | null
  progressReportTrigger: ReportingFrequencyTrigger | null
  progressReportValue: number | null
  progressReportUnit: ReportingTimeUnit | null
  progressReportSteps: ProjectReportStep[]
  shiftCycleOnInterruption: boolean | null
}

export interface ProjectReportStep {
  id?: number
  thresholdHours: number
  reportType: string
}

interface ProjectLessonInfoSettings {
  examOwner: ExamTypeOwner
  minCapacityB1Route: number | null
  maxCapacityB1Route: number | null
  minCapacityZRoute: number | null
  maxCapacityZRoute: number | null
}

interface ProjectHoliday {
  id: number
  title: string
  startDate: string
  endDate: string
}

interface ProjectHolidaysResponse {
  data: ProjectHoliday[]
}

interface ProjectsLocations {
  city: string
  locations: {
    id: number
    name: string
  }[]
}

interface ProjectLocationAddress {
  city: string
  houseNumber: string
  postalCode: string
  street: string
}

interface ProjectLocationItem {
  id: number
  name: string
  address: ProjectLocationAddress
}

interface ProjectsLocationsResponse {
  data: ProjectsLocations[]
}

type Project = {
  id: number
  name: string
  type: ProjectType
  targetGroup: ProjectTargetGroup[]
  routes?: ProjectRoute[]

  isPiggybackProject: boolean
  organizations: Organization[]

  mainProjectName: string | null
  mainOrganizationName: string | null
  mainProjectId: string | null
  mainOrganizationId: string | null

  startDate: string
  endDate: string
  archivedAt?: string | null

  hasExtensionOptions: boolean
  extensionOptionsDescription: string | null

  hasParticipation: boolean
  participationSubcontractor: Subcontractor | null
  hasPvt: boolean
  pvtSubcontractor: Subcontractor | null
  hasMap: boolean
  mapSubcontractor: Subcontractor | null

  subsidyRegulation: SubsidyRegulation[] | null

  isEntrySourceRequired: boolean | null
  isBsnRequired: boolean
  isStudentFileNumberRequired: boolean
  hasLanguageRequirement: boolean | null

  intakeDeadlineWorkdays: number
  lessonDeadlineWorkdays: number
  lessonDeadlineTrigger: LessonDeadlineTrigger | null

  billingDetails: string | null
  indexationEnabled: boolean
  indexationDeadline: string | null

  reportSettings: ProjectReportSettings | null
  lessonInfoSettings: ProjectLessonInfoSettings | null

  locations: {
    data: ProjectLocationItem[]
  }
  holidays: ProjectHolidaysResponse

  regionManager: RegionManager | null
  projectCoordinator: ProjectCoordinator | null
  projectPlanners: ProjectPlanner[]
  administrativeEmployees: AdministrativeEmployee[]
  teamEmail?: string | null
  teamPhoneNumber?: string | null
}

type Projects = Project[]

interface PaginatedProjectsResponse {
  data: {
    projects: Projects
  }
  meta: MetaData
}

interface ProjectByIdResponse {
  data: Project
}

interface ProjectDocuments extends Document {
  projectId: Project["id"]
  uploadType: string
}

interface ProjectDocumentsResponse {
  data: {
    contracts: ProjectDocuments[]
  }
}

export const projectReportCategory = z.enum(
  [
    // "standard_report_toptaal",
    "amsterdam_b1_route",
    // "amsterdam_b1_route_mbo_entree_phase_0",
    // "amsterdam_b1_route_mbo_entree_phase_1",
    // "amsterdam_b1_route_mbo_entree_phase_2",
    // "taaloffensief_perceel_1_tidb",
    // "taaloffensief_perceel_1_tob",
    // "taaloffensief_perceel_2",
    // "rotterdam",
    // "toptaal_in_het_bedrijf",
  ],
  {
    message: "Dit veld is verplicht",
  },
)

export const projectReportTranslations: {
  [key in z.infer<typeof projectReportCategory>]: string
} = {
  // standard_report_toptaal: "Standaard rapportages TopTaal",
  amsterdam_b1_route: "Amsterdam B1-route",
  // amsterdam_b1_route_mbo_entree_phase_0:
  //   "Amsterdam B1-route mbo entree; Fase 0",
  // amsterdam_b1_route_mbo_entree_phase_1:
  //   "Amsterdam B1-route mbo entree; Fase 1",
  // amsterdam_b1_route_mbo_entree_phase_2:
  //   "Amsterdam B1-route mbo entree; Fase 2",
  // taaloffensief_perceel_1_tidb: "Taaloffensief perceel 1 TidB",
  // taaloffensief_perceel_1_tob: "Taaloffensief perceel 1 TOB",
  // taaloffensief_perceel_2: "Taaloffensief perceel 2",
  // rotterdam: "Rotterdam",
  // toptaal_in_het_bedrijf: "TopTaal in het Bedrijf",
}

export const projectReportOptions = Object.entries(
  projectReportTranslations,
).map(([value, label]) => ({
  value,
  label,
}))

export type ProjectReportCategory = z.infer<typeof projectReportCategory>

// Shared with the dynamic report form registry so report identity stays aligned
// with the short values persisted by the backend.
export const progressReport200HoursType = "progress_b1_regular_200_hours"
export const progressReport400HoursType = "progress_b1_regular_400_hours"
export const endB1RegularType = "end_b1_regular"
export const scaleDownA2Type = "scale_down_a2"
export const intakeReportType = "intake_toptaal"

export const projectReportCategoryItems: Record<
  ProjectReportCategory,
  { value: string; label: string }[]
> = {
  // standard_report_toptaal: [
  //   { value: "intake_toptaal", label: "Intakerapport TopTaal" },
  //   {
  //     value: "end_b1_route_june_2026",
  //     label: "Eindrapportage B1-route - juni 2026",
  //   },
  //   {
  //     value: "end_z_route_october_2025",
  //     label: "Eindrapportage Z-route - oktober 2025",
  //   },
  //   {
  //     value: "scale_down_a2_b1_route_june_2024",
  //     label: "Format Rapportage Afschalen naar A2 in de B1-route -jun 2024",
  //   },
  //   {
  //     value: "change_route_june_2024",
  //     label: "Format Rapportage wijzigen leerroute -jun 2024",
  //   },
  // ],
  amsterdam_b1_route: [
    {
      value: intakeReportType,
      label: "Intakerapport TopTaal Inburgering",
    },
    {
      value: progressReport200HoursType,
      label: "Voortgangsrapportage B1 regulier 200 uur",
    },
    {
      value: progressReport400HoursType,
      label: "Voortgangsrapportage B1 regulier 400 uur",
    },
    {
      value: endB1RegularType,
      label: "Eindrapportage B1-route regulier",
    },
    {
      value: scaleDownA2Type,
      label: "Op- of afschaalrapportage (wijzigen leerroute)",
    },
  ],
  // amsterdam_b1_route_mbo_entree_phase_0: [
  //   {
  //     value: "intake_mbo_phase_0",
  //     label: "0.0 Intakerapportage  Fase 0 Doorstroom mbo-route",
  //   },
  //   {
  //     value: "progress_mbo_phase_0_100_hours",
  //     label: "0.0a Voortgangsrapportage Fase 0 100 uur B1-route doorstroom B1-mbo",
  //   },
  //   {
  //     value: "end_transfer_mbo_phase_0",
  //     label: "0.0b Eind- en overdrachtsrapportage Fase 0  B1-route doorstroom B1-mbo",
  //   },
  // ],
  // amsterdam_b1_route_mbo_entree_phase_1: [
  //   {
  //     value: "intake_mbo_phase_1",
  //     label: "0.1 Intakerapportage Fase 1 voorbereidende taalschakel B1 route mbo. Maart 2025",
  //   },
  //   {
  //     value: "progress_mbo_phase_1",
  //     label: "0.2 Voortgangsrapportage Fase 1 voorbereidende taalschakel B1 mbo route. Maart 2025",
  //   },
  //   {
  //     value: "end_mbo_phase_1",
  //     label: "0.3 Eindrapportage Fase 1 voorbereidende taalschakel B1 mbo route. Maart 2025",
  //   },
  // ],
  // amsterdam_b1_route_mbo_entree_phase_2: [
  //   {
  //     value: "intake_mbo_phase_2",
  //     label: "04. Intakerapportage Fase 2 geïntegreerd mbo entree- en inburgeringstraject B1 route mbo. Maart 2025",
  //   },
  //   {
  //     value: "progress_mbo_phase_2",
  //     label: "05. Voortgangsrapportage Fase 2 mbo+inburgering B1 mbo route. Maart 2025",
  //   },
  //   {
  //     value: "end_mbo_phase_2",
  //     label: "06. Eindrapportage Fase 2 geïntegreerde entree + inburgering B1 route mbo. Aug 2025",
  //   },
  // ],
  // taaloffensief_perceel_1_tidb: [
  //   {
  //     value: "intake_language_neighborhood",
  //     label: "Format intakerapportage Taal in de buurt",
  //   },
  //   {
  //     value: "end_language_neighborhood",
  //     label: "Format eindrapportage Taal in de buurt",
  //   },
  // ],
  // taaloffensief_perceel_1_tob: [
  //   { value: "intake_tob", label: "Format intakerapportage TOB" },
  //   {
  //     value: "progress_tob_first",
  //     label: "Format 1e Voortgangsrapportage - Digitaal",
  //   },
  //   {
  //     value: "progress_tob_second",
  //     label: "Format 2e Voortgangsrapportage - Digitaal",
  //   },
  //   { value: "end_tob", label: "Format Eindrapportage TOB - Digitaal" },
  // ],
  // taaloffensief_perceel_2: [
  //   {
  //     value: "intake_work_education",
  //     label: "Intakerapportage werk en opleiding",
  //   },
  //   {
  //     value: "progress_work_education",
  //     label: "Voortgangsrapportage werk en opleiding",
  //   },
  //   {
  //     value: "end_work_education",
  //     label: "Eindrapportage werk en opleiding",
  //   },
  // ],
  // rotterdam: [
  //   {
  //     value: "progress_b1_rotterdam",
  //     label: "FORMAT Voortgangsrapportage B1 gemeente Rotterdam versie mei 2025",
  //   },
  //   {
  //     value: "end_b1_work_rotterdam",
  //     label: "03 Eindrapportage B1 naar werk Rotterdam",
  //   },
  // ],
  // toptaal_in_het_bedrijf: [
  //   { value: "progress_tihb", label: "Voortgangsrapportage TihB NIEUW" },
  //   { value: "end_tihb", label: "Eindrapportage TihB april 2026" },
  // ],
}

// Single source of truth for report item labels, keyed by the short value
// persisted by the backend. Derived from projectReportCategoryItems so
// labels aren't duplicated wherever a report value needs a display label
// (e.g. reportVariantLabels in reports.ts).
export const projectReportItemLabels: Record<string, string> =
  Object.fromEntries(
    Object.values(projectReportCategoryItems)
      .flat()
      .map(({ value, label }) => [value, label]),
  )

export type {
  Project,
  ProjectsLocations,
  RegionManager,
  ProjectCoordinator,
  ProjectPlanner,
  AdministrativeEmployee,
  ProjectType,
  PaginatedProjectsResponse,
  ProjectByIdResponse,
  ProjectsLocationsResponse,
  ProjectDocumentsResponse,
}
